/**
 * PrivacyPage — public privacy policy page.
 *
 * Rendered OUTSIDE DatabaseProvider so it's accessible without login.
 * Ported from v1's src/pages/privacy.tsx with v2 Tailwind styling.
 */

import { useTranslation } from "react-i18next"
import ArrowLeftIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/arrow-left.svg?react"

export function PrivacyPage() {
  const { t } = useTranslation()

  return (
    <div className="h-dvh overflow-auto bg-background px-6 py-4 sm:px-10">
      <div className="mx-auto max-w-2xl">
        <a
          href="/"
          className="mb-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon className="size-3.5" />
          {t("Go to To To-Do")}
        </a>

        <h1 className="mb-6 text-2xl font-bold">{t("Privacy Policy")}</h1>

        <div className="prose prose-sm max-w-none text-foreground [&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-lg [&_h2]:font-semibold [&_p]:mb-3 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-muted-foreground [&_i]:text-foreground">
          <h2>Processing of personal data</h2>

          <p>
            On this day, 18th of January 2024, the following policy has been
            drawn up for Zenta AB.
          </p>

          <p>
            We take care of your privacy. You should be able to feel secure when
            entrusting your personal data to us. For this reason, we have drawn
            up this policy. It is based on current data protection legislation
            and clarifies how we work to safeguard your rights and privacy.
          </p>

          <p>
            The purpose of this policy is to make you aware of how we process
            your personal data, what we use them for, who may view them and under
            what conditions, and how you can safeguard your rights.
          </p>

          <h2>We are personal data controllers</h2>

          <p>
            Zenta AB with company registration number 556835-6850 and address
            Oxtorget 4, 352 32 Växjö is responsible for the personal data
            processed about you. Zenta AB is therefore the data controller for
            your personal data.
          </p>

          <h2>Why do we process your personal data?</h2>

          <p>
            We process your personal data in order to provide you with our
            services and products, i.e. only to enter into or fulfil a contract
            with you as a customer.
          </p>

          <h2>What data do we collect about you – and why?</h2>

          <p>
            We strive to process as little personal data about you as possible.
            This means that we do not collect any more personal data than is
            necessary to be able to enter into or fulfil contracts with you. In
            order to be able to enter into and fulfil contracts with you, we need
            data about your e-mail address. We do not have more access to your
            personal data than the data that you have provided to us.
          </p>

          <p>
            If you have not given us your consent, we do not use your personal
            data for marketing purposes.
          </p>

          <h2>How do we protect your personal data?</h2>

          <p>
            Your security is important to us. Therefore, we have taken
            appropriate technical, organisational and administrative security
            measures to protect your personal data from unauthorised access and
            other unauthorised processing. We regularly analyse and evaluate the
            measures in order to ensure that protection of your data is as safe
            as possible.
          </p>

          <h2>Who do we disclose your data to?</h2>

          <p>
            We do not disclose your data to other companies or organisations
            unless required by law or necessary to perform our statutory or
            contractual obligations towards you.
          </p>

          <p>
            We may disclose your personal data to any of our partners, suppliers
            or subcontractors, but only if that is necessary for us to meet our
            obligations in relation to you as a customer. We never provide more
            personal data than is necessary.
          </p>

          <p>
            When required by law, we may need to disclose your data to public
            authorities and other organisations. We may also need to disclose
            your data if it is necessary to exercise, establish or monitor our
            legal claims.
          </p>

          <p>
            We never disclose your personal data to other companies or businesses
            for marketing purposes.
          </p>

          <h2>On what legal basis do we process your personal data?</h2>

          <p>
            We only process the personal data that we need to enable us to
            perform our services or deliver our products to you, i.e. when we
            enter into or fulfil a contract with you. To the extent that we
            process further data, we will obtain your consent or ensure that
            processing is authorised on a different legal basis.
          </p>

          <h2>How long do we process your personal data?</h2>

          <p>
            We retain data about you as a customer for the duration of the
            agreement with you and for a reasonable time thereafter. In most
            cases, this means that we do not retain your personal data for a
            period of more than one year from the end of the agreement, but under
            certain conditions we may keep your data for a longer period of time.
            This applies, for example, when required by law or when the data may
            be needed to establish, exercise and monitor legal claims.
          </p>

          <h2>Your rights</h2>

          <p>
            When we process personal data about you, you as registered have a
            number of rights. You have the right to contact us at any time with
            respect to these, and if you wish to exercise any of the rights
            described below, you can reach us most easily at info@zenta.se.
          </p>

          <p>
            We reserve the right to take appropriate protective and security
            measures in order to ensure that you are the person you claim to be
            when you contact us. If you cannot satisfactorily demonstrate your
            identity, we may not be able to respond to your request.
          </p>

          <p>
            <em>Access to personal data</em>
          </p>

          <p>
            You have the right to know what personal data we process about you.
            If you wish to know, you can get a compiled register extract from us
            that contains all the personal data we process about you.
          </p>

          <p>
            <em>Correction and deletion</em>
          </p>

          <p>
            If we process your personal data incorrectly or if we no longer need
            the data, you are entitled to have it deleted. If the data is
            incomplete, you have the right to have it supplemented. Please keep
            in mind that we may not be able to provide you with our services if
            you request to have your personal data deleted.
          </p>

          <p>
            <em>Data portability</em>
          </p>

          <p>
            In certain circumstances you have the right to receive the data that
            we process about you in a general, written, machine-readable and
            structured format. You have the right to do so for the personal data
            that you have provided to us and which we process with your consent
            or when the personal data is required to enter into or complete an
            agreement.
          </p>

          <p>
            <em>Restrictions to processing</em>
          </p>

          <p>
            Under certain conditions, you have the right to request that we
            restrict our processing of your data. This means that we mark the
            data so that in future we only process it for certain specific
            purposes. We may not be able to provide you with our services if we
            restrict the processing of your personal data.
          </p>

          <p>
            <em>Right to make objections</em>
          </p>

          <p>
            You have the right to object to the processing of personal data that
            is carried out to perform a task in the public interest, as part of
            an exercise by a public authority or after a balance of interests. We
            do not process your personal information for any of these purposes or
            on any of these grounds. Therefore, you cannot direct any objections
            towards our processing on this basis.
          </p>

          <p>
            <em>Right to lodge a complaint</em>
          </p>

          <p>
            You have the right to lodge a complaint with the Swedish Data
            Protection Authority if you believe that we are processing your
            personal data in an inappropriate manner. You can read more about
            this on the Swedish Data Protection Authority&apos;s website{" "}
            <a
              href="https://www.Integritetsskyddsmyndigheten.se"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              www.Integritetsskyddsmyndigheten.se
            </a>
            .
          </p>

          <h2>Contact</h2>

          <p>
            You can contact us at any time by writing to{" "}
            <a
              href="mailto:info@zenta.se"
              className="text-primary hover:underline"
            >
              info@zenta.se
            </a>{" "}
            or by calling us on phone number 0470-123 00 if you wish to learn
            more about how we process your personal data. You can also read more
            about us on our website{" "}
            <a
              href="https://www.zenta.se"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              www.zenta.se
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
